import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { CSSProperties } from 'react';

/**
 * Inline text formats a mention can carry — the subset of Lexical's
 * `TextFormatType` the toolbar exposes (bold / italic / underline /
 * strikethrough). Stored as string tokens rather than Lexical's numeric format
 * bitmask so the serialized contract doesn't couple to Lexical's internal bit
 * values.
 */
export type MentionFormat = 'bold' | 'italic' | 'underline' | 'strikethrough';

const FORMAT_ORDER: readonly MentionFormat[] = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
];
const FORMAT_SET = new Set<string>(FORMAT_ORDER);

// Semantic tag emitted per format on export — the `<b>/<i>/<u>/<s>` wrappers the
// web renderer (and mojo/native) already understand for formatted text.
const FORMAT_TAG: Record<MentionFormat, string> = {
  bold: 'b',
  italic: 'i',
  underline: 'u',
  strikethrough: 's',
};

// Inverse map for import — accept terse (`<b>`) and semantic (`<strong>`)
// spellings alike.
const TAG_FORMAT: Record<string, MentionFormat> = {
  B: 'bold',
  STRONG: 'bold',
  I: 'italic',
  EM: 'italic',
  U: 'underline',
  S: 'strikethrough',
  STRIKE: 'strikethrough',
  DEL: 'strikethrough',
};

/** True when a Lexical `TextFormatType` is one a mention can carry. */
export function isMentionFormat(format: string): format is MentionFormat {
  return FORMAT_SET.has(format);
}

/** Dedupe and canonically order a set of formats (stable serialization). */
function normalizeFormats(formats: readonly MentionFormat[]): MentionFormat[] {
  return FORMAT_ORDER.filter((f) => formats.includes(f));
}

/** The mention's formats as an inline style for the rendered chip. */
function formatToStyle(formats: readonly MentionFormat[]): CSSProperties {
  const decoration = [
    formats.includes('underline') ? 'underline' : '',
    formats.includes('strikethrough') ? 'line-through' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return {
    // `.lexical-mention` sets font-weight: 500, so bold must render at 700 to
    // read as visually distinct.
    fontWeight: formats.includes('bold') ? 700 : undefined,
    fontStyle: formats.includes('italic') ? 'italic' : undefined,
    textDecoration: decoration || undefined,
  };
}

/**
 * Read the inline formats wrapping a mention span on import by walking the
 * contiguous chain of formatting ancestors (`<b><i>…<span>token</span>…`).
 * Stops at the first non-formatting ancestor, so it only picks up the wrappers
 * exportDOM produced.
 */
function readAncestorFormats(el: HTMLElement): MentionFormat[] {
  const found: MentionFormat[] = [];
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const format = TAG_FORMAT[cur.tagName];
    if (!format) break;
    if (!found.includes(format)) found.push(format);
    cur = cur.parentElement;
  }
  return normalizeFormats(found);
}

export type SerializedMentionNode = Spread<
  {
    mentionId: string;
    mentionName: string;
    refType: string;
    formats: MentionFormat[];
  },
  SerializedLexicalNode
>;

const MENTION_REF_REGEX = /@\{ref:user::id:(\d+)::name:([^}]+)\}/;
export class MentionNode extends DecoratorNode<JSX.Element> {
  mentionId: string;
  mentionName: string;
  refType: string;
  formats: MentionFormat[];

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.mentionId,
      node.mentionName,
      node.refType,
      node.formats,
      node.__key
    );
  }

  constructor(
    mentionId = '',
    mentionName = '',
    refType = 'user',
    formats: readonly MentionFormat[] = [],
    key?: NodeKey
  ) {
    super(key);
    this.mentionId = mentionId;
    this.mentionName = mentionName;
    this.refType = refType;
    this.formats = normalizeFormats(formats);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const { mentionId, mentionName, refType, formats } = serializedNode;
    return $createMentionNode(mentionId, mentionName, refType, formats ?? []);
  }

  exportJSON(): SerializedMentionNode {
    return {
      type: 'mention',
      version: 1,
      mentionId: this.mentionId,
      mentionName: this.mentionName,
      refType: this.refType,
      formats: this.getFormats(),
    };
  }

  // ── Format accessors ────────────────────────────────────────────────────
  getFormats(): MentionFormat[] {
    return [...this.getLatest().formats];
  }

  hasFormat(format: MentionFormat): boolean {
    return this.getLatest().formats.includes(format);
  }

  setFormats(formats: readonly MentionFormat[]): this {
    const self = this.getWritable();
    self.formats = normalizeFormats(formats);
    return self;
  }

  /** Add or remove a single format; `force` sets the target state explicitly. */
  toggleFormat(format: MentionFormat, force?: boolean): this {
    const next = force ?? !this.hasFormat(format);
    const set = new Set(this.getLatest().formats);
    if (next) set.add(format);
    else set.delete(format);
    return this.setFormats([...set]);
  }

  // called when the editor is initalized
  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'lexical-mention';
    span.setAttribute('data-type', 'mention');
    span.setAttribute('data-id', this.mentionId);
    span.setAttribute('data-name', this.mentionName);
    span.setAttribute('data-ref-type', this.refType);
    return span;
  }

  updateDOM(): false {
    return false;
  }

  /**
   * Gets called by lexical when we get the HTML value from it,
   * Replaces the user-friendly mention span with raw `@{ref:...}` text.
   * Any formats are emitted as semantic `<b>/<i>/<u>/<s>` wrappers around the
   * token span (Option A) — the token grammar itself is left unchanged.
   */
  exportDOM(): DOMExportOutput {
    const span = document.createElement('span');
    span.textContent = `@{ref:${this.refType}::id:${this.mentionId}::name:${this.mentionName}}`;
    // Wrap innermost→outermost in canonical order so nesting is stable.
    let element: HTMLElement = span;
    this.formats.forEach((format) => {
      const wrapper = document.createElement(FORMAT_TAG[format]);
      wrapper.appendChild(element);
      element = wrapper;
    });
    return { element };
  }

  /**
   * Does the opposite, creates mention nodes from backend values, recovering any
   * surrounding `<b>/<i>/<u>/<s>` wrappers as the node's formats.
   */
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        const text = domNode.textContent || '';
        if (!MENTION_REF_REGEX.test(text)) return null;

        return {
          conversion: (element: HTMLElement): DOMConversionOutput | null => {
            const content = element.textContent || '';
            const match = MENTION_REF_REGEX.exec(content);
            if (!match) return null;

            return {
              node: $createMentionNode(
                match[1],
                match[2],
                'user',
                readAncestorFormats(element)
              ),
            };
          },
          priority: 1,
        };
      },
    };
  }

  getTextContent(): string {
    return `@${this.mentionName}`;
  }

  isInline(): boolean {
    return true;
  }

  decorate(): JSX.Element {
    return (
      <span
        className="lexical-mention"
        data-mention-id={this.mentionId}
        style={formatToStyle(this.formats)}
      >
        @{this.mentionName}
      </span>
    );
  }
}

export function $createMentionNode(
  id: string,
  name: string,
  refType = 'user',
  formats: readonly MentionFormat[] = []
): MentionNode {
  return new MentionNode(id, name, refType, formats);
}

export function $isMentionNode(node: LexicalNode): node is MentionNode {
  return node instanceof MentionNode;
}
